import { HARDENING_CLUSTERS, HARDENING_POLICY_STATUS } from '@/lib/constants/hardening'
import { MDM_CHECK_NOT_CHECKED } from '@/lib/constants/mdm/policies'

export const MDM_DASHBOARD = {
  TITLE: 'Panel MDM',
  SUMMARY: (enrolled: number, online: number, offline: number) =>
    `${enrolled} ${enrolled === 1 ? 'equipo inscrito' : 'equipos inscritos'} · ${online} en línea · ${offline} desconectados`,
  SHORTCUTS: {
    NEW_GROUP: 'Nuevo grupo',
    NEW_POLICY: 'Nueva política',
    ADD_SOFTWARE: 'Añadir software',
    RUN_SCRIPT: 'Ejecutar script',
  },
  COVERAGE: {
    TITLE: 'Cobertura MDM',
    SUBTITLE: 'Dispositivos del inventario TI inscritos en MDM',
    ENROLLED: 'inscritos',
    LAPTOPS: 'Portátiles',
    PHONES: 'Móviles',
    PENDING: (count: number) => `${count} pendientes de inscripción`,
  },
  BASELINE: {
    TITLE: 'Línea base de seguridad',
    SUBTITLE: (compliant: number, total: number) =>
      `${compliant} de ${total} ${total === 1 ? 'equipo cumple' : 'equipos cumplen'} todos los controles`,
    COMPLIANT: 'cumplen',
    VIEW_HOSTS: 'Ver equipos',
    FAILED: (count: number) => `${count} ${count === 1 ? 'fallido' : 'fallidos'}`,
    ALL_PASS: 'Todos cumplen',
    WARNING: (count: number) => `${count} ${count === 1 ? 'aviso' : 'avisos'}`,
    SEPARATOR: ' · ',
  },
  NON_COMPLIANT_HOSTS: {
    TITLE: 'Equipos sin cumplimiento',
    OPEN_HOSTS: 'Abrir equipos',
    EMPTY: 'Todos los equipos cumplen la línea base',
  },
  MORE_ROWS: (count: number) => `+${count} más`,
  VIEW_ALL: 'Ver todos',
  NOT_ENROLLED: {
    TITLE: 'Sin inscribir',
    OPEN_IT_INVENTORY: 'Abrir inventario TI',
    PENDING: 'Pendiente',
    NO_AGENT: 'Agente MDM no instalado',
    EMPTY: 'Todos los dispositivos están inscritos',
  },
  HOSTS_BREAKDOWN: {
    TITLE: 'Equipos',
    ENROLLED: (count: number) => `${count} ${count === 1 ? 'equipo inscrito' : 'equipos inscritos'}`,
    ONLINE_SECTION: 'En línea',
    OFFLINE_SECTION: 'Desconectados',
    STATUS_NOT_SUPPORTED: 'No compatible',
    COLUMN_HOST: 'Equipo',
    COLUMN_GROUPS: 'Grupos',
    COLUMN_ASSIGNED_TO: 'Asignado a',
    COLUMN_LAST_CHECK_IN: 'Última conexión',
    UNASSIGNED: 'Sin asignar',
    COMPLIANCE_PASS: 'Cumple',
    COMPLIANCE_FAIL: 'No cumple',
    COMPLIANCE_PENDING: 'Pendiente',
    COMPLIANCE_ACTION_REQUIRED: 'Acción requerida',
    COMPLIANCE_ACTION_REQUIRED_INFO:
      'Pendiente del usuario: debe cerrar sesión y volver a entrar, o reiniciar, para que el equipo termine el cifrado del disco con su contraseña y guarde la clave de recuperación.',
    COMPLIANCE_NOT_APPLICABLE: '-',
    NO_POLICIES: 'Sin políticas',
    FILTER_BY_GROUP: 'Filtrar por grupo',
    DEVICE_TYPE_ALL: 'Todos',
    DEVICE_TYPE_LAPTOPS: 'Portátiles',
    DEVICE_TYPE_MOBILES: 'Móviles',
    SEARCH_PLACEHOLDER: 'Buscar equipos o grupos...',
    DRAWER: {
      SECTION_COMPLIANCE: 'Cumplimiento',
      SECTION_MANAGEMENT: 'Gestión',
      SECTION_POLICIES: (count: number) => `Políticas del dispositivo (${count})`,
      SECTION_APPLICATIONS: 'Aplicaciones',
      SECTION_MORE_DETAILS: 'Más detalles',
      SECTION_ACTIONS: 'Acciones',
      POSTURE: 'Postura',
      PHONE_ENCRYPTION: 'Integrado',
      PHONE_ENCRYPTION_INFO:
        'Los móviles cifran su almacenamiento de serie: el iPhone activa la Protección de datos al establecer un código, y Android 10 o posterior siempre usa cifrado basado en archivos. Exigir el código es lo que lo protege.',
      NOT_APPLICABLE_INFO: {
        [HARDENING_CLUSTERS.PRIVILEGE_PROTECTION]:
          'Los móviles no tienen cuentas de administrador que limitar: cada app se ejecuta aislada, sin permisos de administrador.',
        [HARDENING_CLUSTERS.HOST_FIREWALL]:
          'Los móviles no tienen cortafuegos que configurar: las apps se ejecutan aisladas, sin acceso entre ellas ni al sistema.',
      } as Record<string, string>,
      POLICIES_APPLIED: (applied: number, total: number) => `${applied} de ${total} aplicadas`,
      IT_INVENTORY_LINK: 'Ver en Inventario TI',
      IT_INVENTORY_LINK_DETAIL: 'Hardware y almacenamiento',
      DISK_FREE: (free: string, total: string) => `${free} libres de ${total}`,
      DISK_TOTAL: (total: string) => `${total} en total`,
      DISK_UNKNOWN: 'El dispositivo no lo informa',
      APPLICATIONS_COUNT: (count: number) => `${count} instaladas`,
      APPLICATIONS_EMPTY: 'El dispositivo no informa ninguna aplicación.',
      APPLICATIONS_SEARCH_PLACEHOLDER: 'Buscar aplicaciones',
      APPLICATIONS_NO_RESULTS: 'Ninguna aplicación coincide con la búsqueda.',
      APPLICATIONS_ERROR: 'No se pudieron cargar las aplicaciones de este dispositivo.',
      APPLICATIONS_HINT_ANDROID:
        'Solo se listan las aplicaciones del perfil de trabajo. Las del lado personal del teléfono no son visibles para el MDM.',
      NOTIFY_USER: 'Avisar al usuario para que actúe',
      NOTIFY_USER_DIALOG_TITLE: 'Avisar al usuario',
      NOTIFY_USER_DIALOG_DESCRIPTION: (hostname: string) =>
        `Aparecerá un mensaje en ${hostname} pidiendo al usuario que cierre sesión y vuelva a entrar, o que reinicie. MacOS necesita su contraseña una vez para terminar el cifrado del disco.`,
      NOTIFY_USER_DIALOG_PREVIEW_LABEL: 'El usuario verá',
      NOTIFY_USER_CANCEL: 'Cancelar',
      NOTIFY_USER_CONFIRM: 'Avisar al usuario',
      NOTIFY_USER_SENDING: 'Enviando...',
      NOTIFY_USER_SENT: 'Mensaje enviado al usuario',
      NOTIFY_USER_FAILED: 'No se pudo avisar al usuario',
      DISK_ENCRYPTION_NOTICE_TITLE: 'Cifrado de disco',
      DISK_ENCRYPTION_NOTICE_MESSAGE:
        'Tu organización está activando el cifrado de disco en este Mac. Para terminar, cierra sesión y vuelve a entrar, o reinicia. Solo tienes que hacerlo una vez.',
    },
  },
  CHECKS: {
    [HARDENING_CLUSTERS.PASSWORD_SECURITY]: 'Política de contraseñas',
    [HARDENING_CLUSTERS.DISK_ENCRYPTION]: 'Cifrado de disco',
    [HARDENING_CLUSTERS.SCREEN_LOCK]: 'Bloqueo de pantalla',
    [HARDENING_CLUSTERS.PRIVILEGE_PROTECTION]: 'Protección de privilegios',
    [HARDENING_CLUSTERS.HOST_FIREWALL]: 'Cortafuegos',
  },
  CHECK_STATES: {
    [HARDENING_POLICY_STATUS.PASSED]: 'Cumple',
    [HARDENING_POLICY_STATUS.WARNING]: 'Aviso',
    [HARDENING_POLICY_STATUS.FAILED]: 'No cumple',
    [HARDENING_POLICY_STATUS.NOT_APPLICABLE]: 'No aplica',
    [MDM_CHECK_NOT_CHECKED]: 'Sin comprobar',
  },
  POLICY_POSTURE: {
    COMPLIANT: 'En cumplimiento',
    NON_COMPLIANT: 'Sin cumplimiento',
  },
}
